// Vuex store
import store from "@/core/services/store";
// moment.js
import moment from "moment";
// Models
import { IDriver } from "@/core/_models/driver.model";
import { IQuarryAssignment } from "@/core/_models/quarryAssignment.model";
import { DeliveryDateType } from "@/core/_constants/deliveryDateTypes.constant";
import {
  CreateOrUpdateJobDriverModel,
  CreateOrUpdateJobModel,
  JobsClient,
  JobModel,
  JobSearchFilter,
  UpdateJobModel,
  CancelJobModel,
  CompleteJobModel,
  DriversClient,
  DriverModel,
  UpdateDeliveryDetailsModel,
  MoveJobModel
} from "@/core/api/apiClient";
import { BIconLayoutTextSidebar } from "bootstrap-vue";

const jobsClient = new JobsClient();

const JobsService = {
  /**
   * Gets all Jobs.
   *
   * @param clientId Client's ID
   * @returns {Promise<IJob[] | any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async getAllJobs(clientId: string): Promise<JobModel[] | any> {
    var filter = new JobSearchFilter({
      includeCanceled: true,
      includeCompleted: true,
      isCompleted: false,
      dateEnd: undefined,
      dateStart: undefined,
      customerName: undefined,
      jobSite: undefined
    });

    return await jobsClient
      .searchJobs(clientId, filter)
      .then((jobResult) => {
        const data: JobModel[] = [];
        jobResult.model.forEach((job) => {
          let element = job as JobModel;
          element = this.fixJobs(element);

          data.push(element);
        });
        return data;
      })
      .catch((error) => {
        console.error(error);
      });
  },

  /**
   * Gets Jobs in a specified date range.
   *
   * @param clientId Client's ID
   * @param start Start date
   * @param end End date
   * @returns {Promise<JobModel[] | any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async getJobsDateRange(
    clientId: string,
    start: Date,
    end: Date,
    ordered?: boolean,
    includeCompleted?: boolean,
    includeCanceled?: boolean
  ): Promise<JobModel[] | any> {
    var filter = new JobSearchFilter({
      includeCanceled: includeCanceled ?? false,
      includeCompleted: includeCompleted ?? false,
      isCompleted: undefined,
      dateEnd: end,
      dateStart: start,
      customerName: undefined,
      jobSite: undefined
    });
    return await jobsClient
      .searchJobs(clientId, filter)
      .then((jobResult) => {
        let data: JobModel[] = jobResult.model;

        // * Sort by DeliveryDetails.DeliveryDate
        if (ordered) {
          data = data.sort((a, b) => {
            const dateA = a.deliveryDetails?.deliveryDate?.getTime() ?? Infinity;
            const dateB = b.deliveryDetails?.deliveryDate?.getTime() ?? Infinity;

            return dateA - dateB;
          });
        }

        return data;
      })
      .catch((error) => {
        console.error(error);
      });
  },

  /**
   * Gets all non-completed Jobs.
   *
   * @param clientId Client's ID
   * @returns {Promise<IJob[] | any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async getAllActiveJobs(clientId: string): Promise<JobModel[] | any> {
    var filter = new JobSearchFilter({
      includeCanceled: false,
      includeCompleted: false,
      isCompleted: false,
      dateEnd: undefined,
      dateStart: undefined,
      customerName: undefined,
      jobSite: undefined
    });

    const jobData = await jobsClient
      .searchJobs(clientId, filter)
      .then((jobResult) => {
        const activeJobs: JobModel[] = [];

        jobResult.model.forEach((job) => {
          let element = job as JobModel;

          element = this.fixJobs(element);

          if (element.canceled) return;
          activeJobs.push(element);
        });
        return activeJobs;
      })
      .catch((error) => {
        console.error(error);
        return [];
      });

    // Generate dateMapper and resulting Dashboard Jobs.
    const dateMapper: any = this.generateDateMapper(jobData);
    const resultJobs: JobModel[] = this.generateJobDashboard(dateMapper, jobData);

    // Update store and return sortedData
    store.dispatch("SET_ACTIVE_JOBS", resultJobs);
    return resultJobs;
  },
  normalizeUtcStartOfDay(date: Date): string {
    return new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
    ).toISOString(); //
  },
  generateDateMapper(activeJobs: JobModel[]): Record<string, JobModel[]> {
    const dateSet = new Set<string>();

    activeJobs.forEach((job: JobModel) => {
      const jobDate = job.deliveryDetails?.deliveryDate;
      if (!jobDate) return;

      dateSet.add(this.normalizeUtcStartOfDay(jobDate));
    });

    const sortedKeys = Array.from(dateSet).sort();

    return sortedKeys.reduce((acc, key) => {
      acc[key] = [];
      return acc;
    }, {} as Record<string, JobModel[]>);
  },

  generateJobDashboard(dateMapper: Record<string, JobModel[]>, activeJobs: JobModel[]): JobModel[] {
    const resultDateMapper = { ...dateMapper };

    activeJobs.forEach((job: JobModel) => {
      const jobDate = job.deliveryDetails?.deliveryDate;
      if (!jobDate) return; // skip if null

      const key = this.normalizeUtcStartOfDay(jobDate);
      if (key in resultDateMapper) {
        resultDateMapper[key].push(job);
      }
    });

    return Object.values(resultDateMapper).flat();
  },

  /**
   * Returns Jobs based on parameters.
   * ! Need to optimize this in the future - we're searching on 12K Jobs as of Oct 22, 2021.
   *
   * @param {string} clientId
   * @param {boolean} isCompleted
   * @param {string} searchCustomerName
   * @param {string} searchJobSite
   * @return {Promise<JobModel[] | any>)}
   */
  async searchJobs(
    clientId: string,
    isCompleted: boolean,
    searchCustomerName: string,
    searchJobSite: string
  ): Promise<JobModel[] | any> {
    var filter = new JobSearchFilter({
      includeCanceled: false,
      includeCompleted: false,
      isCompleted: isCompleted,
      dateEnd: undefined,
      dateStart: undefined,
      customerName: searchCustomerName,
      jobSite: searchJobSite
    });

    return await jobsClient
      .searchJobs(clientId, filter)
      .then((jobResult) => {
        let data: JobModel[] = jobResult.model;

        // Sort by DeliveryDate
        data = data.sort((a, b) => {
          const dateA = a.deliveryDetails?.deliveryDate?.getTime() ?? Infinity;
          const dateB = b.deliveryDetails?.deliveryDate?.getTime() ?? Infinity;

          return dateB - dateA;
        });

        return data;
      })
      .catch((error) => {
        console.error(error);
      });
  },

  /**
   * Creates a Job
   *
   * @param {string} clientId Client's ID
   * @param {UpdateJobModel} data The Job.
   * @return {Promise<JobModel[] | any>}  Promise-wrapped response.
   */
  async createJob(clientId: string, data: UpdateJobModel): Promise<JobModel[] | any> {
    return await jobsClient
      .addJob(clientId, data)
      .then(() => {
        const success = {
          status: 200,
          message: "OK"
        };

        return success;
      })
      .catch((error: any) => {
        console.error(error);
      });
  },

  /**
   * Updates a Job
   *
   * @param {string} clientId Client's ID
   * @param {string} jobId  Job ID
   * @param {UpdateJobModel} data The Job.
   * @return {Promise<any>}  Promise-wrapped response.
   */
  async updateJob(clientId: string, jobId: string, data: UpdateJobModel): Promise<any> {
    return await jobsClient
      .updateJob(clientId, jobId, data)
      .then(() => {
        const success = {
          status: 204,
          message: "OK"
        };

        return success;
      })
      .catch((error: any) => {
        console.error(error);
      });
  },
  /**
   * Creates or Updates a Job with assigned quarries
   * @param clientId
   * @param data
   * @returns
   */
  async createOrUpdateJob(clientId: string, data: CreateOrUpdateJobModel): Promise<any> {
    return await jobsClient
      .createOrUpateJobWithAssignments(clientId, data)
      .then(() => {
        const success = {
          status: 204,
          message: "OK"
        };

        return success;
      })
      .catch((error: any) => {
        console.error(error);
      });
  },

  /**
   * Updates the delivery details for a job
   *
   * @param {string} clientId Client's ID
   * @param {string} jobId  Job ID
   * @param {UpdateDeliveryDetailsModel} data The delivery details.
   * @return {Promise<any>}  Promise-wrapped response.
   */
  async updateJobDeliveryDetails(
    clientId: string,
    jobId: string,
    data: UpdateDeliveryDetailsModel
  ): Promise<any> {
    return await jobsClient
      .updateDelivery(clientId, jobId, data)
      .then(() => {
        const success = {
          status: 204,
          message: "OK"
        };

        return success;
      })
      .catch((error: any) => {
        console.error(error);
      });
  },

  /**
   * Updates the scheduled delivery date and driver for a job
   *
   * @param {string} clientId Client's ID
   * @param {string} jobId  Job ID
   * @param {MoveJobModel} data The delivery details.
   * @return {Promise<any>}  Promise-wrapped response.
   */
  async moveJobSchedule(clientId: string, jobId: string, data: MoveJobModel): Promise<any> {
    return await jobsClient
      .moveJob(clientId, jobId, data)
      .then(() => {
        const success = {
          status: 204,
          message: "OK"
        };

        return success;
      })
      .catch((error: any) => {
        console.error(error);
      });
  },

  /**
   * Deletes a Job
   *
   * @param {string} clientId Client's ID
   * @param {IJob} id The Job ID.
   * @return {Promise<any>}  Promise-wrapped response.
   */
  async deleteJob(clientId: string, id: string): Promise<any> {
    return jobsClient
      .deleteJob(clientId, id)
      .then(() => {
        const success = {
          status: 200,
          message: "OK"
        };
        return success;
      })
      .catch((error: any) => {
        console.error(error);
      });
  },

  /**
   * Cancels a Job
   *
   * @param {string} clientId Client's ID
   * @param {JobModel} data The Job to cancel
   * @returns {Promise<any>} Promise-wrapped response.
   * @author Nick Brahmimir
   */
  async cancelJob(clientId: string, data: JobModel): Promise<any> {
    const cancelModel = new CancelJobModel({
      clientId: clientId,
      jobId: data.jobId,
      cancelStatus: true
    });

    return await jobsClient
      .cancelJob(cancelModel)
      .then(() => {
        const success = {
          status: 204,
          message: "OK"
        };
        return success;
      })
      .catch((error: any) => {
        console.error(error);
      });
  },

  /**
   * Reopens (un-cancels) a Job
   *
   * @param {string} clientId The Client's ID
   * @param {JobModel} data The Job to reopen
   * @returns {Promise<any>} Promise-wrapped response
   * @author Nick Brahmimir
   */
  async reopenJob(clientId: string, data: JobModel): Promise<any> {
    const cancelModel = new CompleteJobModel({
      clientId: clientId,
      jobId: data.jobId,
      completeStatus: false,
      cancelStatus: false
    });

    return await jobsClient
      .completeJob(cancelModel)
      .then(() => {
        const success = {
          status: 204,
          message: "OK"
        };
        return success;
      })
      .catch((error: any) => {
        console.error(error);
      });
  },

  /**
   * Completes a Job
   *
   * @param {string} clientId Client's ID
   * @param {JobModel} data The Job to cancel
   * @returns {Promise<any>} Promise-wrapped response.
   * @author Nick Brahmimir
   */
  async completeJob(clientId: string, data: JobModel): Promise<any> {
    const completeModel = new CompleteJobModel({
      clientId: clientId,
      jobId: data.jobId,
      completeStatus: true,
      cancelStatus: data.canceled
    });

    return await jobsClient
      .completeJob(completeModel)
      .then(() => {
        const success = {
          status: 204,
          message: "OK"
        };
        return success;
      })
      .catch((error: any) => {
        console.error(error);
      });
  },

  /**
   * Gets all Driver Users.
   *
   * @param clientId Client's ID
   * @returns {Promise<DriverModel[] | any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async getAllDrivers(clientId: string): Promise<DriverModel[] | any> {
    const driverClient = new DriversClient();

    return await driverClient
      .listForClient(clientId)
      .then((driverResult) => {
        const drivers = driverResult.model;

        drivers.sort((a, b) => {
          const aFirstChar = a.firstName.charAt(0);
          const bFirstChar = b.firstName.charAt(0);
          if (aFirstChar > bFirstChar) {
            return 1;
          } else if (aFirstChar < bFirstChar) {
            return -1;
          } else {
            const aLastChar = a.lastName.charAt(0);
            const bLastChar = b.lastName.charAt(0);
            if (aLastChar > bLastChar) {
              return 1;
            } else if (aLastChar < bLastChar) {
              return -1;
            } else {
              return 0;
            }
          }
        });

        // Update store and return data
        store.dispatch("SET_DRIVERS", drivers);
        return drivers;
      })
      .catch((error) => {
        console.error(error);
      });
  },

  /**
   * Gets a Driver by ID.
   *
   * @param clientId Client's ID
   * @param driverId Driver's ID
   * @returns {Promise<DriverModel | any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async getDriver(clientId: string, driverId: string): Promise<DriverModel | any> {
    const driversClient = new DriversClient();

    await driversClient
      .getDriver(clientId, driverId)
      .then((driverResult) => {
        let data: DriverModel | undefined = undefined;
        if (!driverResult.hasError && driverResult.model) {
          data = driverResult.model as DriverModel;
        }
        return data;
      })
      .catch((error) => {
        console.error(error);
      });
  },

  /**
   * Updates the Driver/Quarry assignments for a job
   *
   * @param clientId Client's ID
   * @param jobId Job's ID
   * @param payload The list of assigned JobDrivers
   * @returns {Promise<any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async createOrUpdateJobDrivers(
    clientId: string,
    jobId: string,
    payload: CreateOrUpdateJobDriverModel[]
  ) {
    await jobsClient
      .assignQuarries(clientId, jobId, payload)
      .then(() => {
        const success = {
          status: 204,
          message: "OK"
        };
        return success;
      })
      .catch((error: any) => {
        console.error(error);
      });
  },

  /**
   * Maps QuarryAssignments to Drivers, should they be missing any.
   *
   * @param {(IDriver[] | undefined)} drivers The array of drivers.
   * @param {IQuarryAssignment[]} quarryAssignments The saved quarry assignments.
   * @return {{IQuarryAssignment[]}} Returns finalized quary assignments.
   * @author Nick Brahimir
   */
  mapQuarryAssignments(
    drivers: IDriver[] | undefined | null,
    quarryAssignments: IQuarryAssignment[]
  ): IQuarryAssignment[] {
    const finalizedAssignments = quarryAssignments;

    // If Job has no Drivers assigned to it; return
    if (!drivers || !drivers.length) return finalizedAssignments;

    // Add any missing QuarryAssignments
    if (drivers.length !== quarryAssignments.length) {
      drivers.forEach((driver) => {
        const driverAssignmentLookup = quarryAssignments.find((a) => a.Driver.UserId === driver.UserId);

        if (!driverAssignmentLookup) {
          const newAssignment: IQuarryAssignment = {
            Driver: driver,
            Quarry: null
          };

          finalizedAssignments.push(newAssignment);
        }
      });
    }

    return finalizedAssignments;
  },

  /**
   * Filters Jobs based on an array of DeliveryDateType criteria, then sorts the Jobs by their
   * DeliveryDate.
   *
   * @param {JobModel[]} data The Job data to process.
   * @param {DeliveryDateType[]} criteria The DeliveryDateType criteria.
   * @return {{JobModel[]}} The resulting array of filtered and sorted Jobs.
   * @author Nick Brahimir
  //  */
  filterAndSortJobs(data: JobModel[], criteria: DeliveryDateType[]): JobModel[] {
    const result = data
      .filter(
        (job) =>
          job.deliveryDetails?.deliveryDateType &&
          criteria.includes(DeliveryDateType[job.deliveryDetails?.deliveryDateType])
      )
      .sort((a, b) => {
        const sortDiff = a.sortIndex - b.sortIndex;
        if (sortDiff !== 0) return sortDiff;

        const dateA = a.deliveryDetails?.deliveryDate?.getTime() ?? 0;
        const dateB = b.deliveryDetails?.deliveryDate?.getTime() ?? 0;
        return dateA - dateB;
      });
    return result;
  },

  /**
   * Fixes Jobs by adding/removing fields in an attempt to normalize Job data in database.
   *
   * @param {JobModel} job The job to be fixed
   * @return {*}  {IJob} The Job with fixed properties
   * @author Nick Brahimir
   */
  fixJobs(job: JobModel): JobModel {
    // * Fix:: job.Jobsite.Name
    // Fix missing "Name" property on job.Jobsite
    if (job.jobSite) {
      if (!job.jobSite.name) job.jobSite.name = job.jobSite.streetAddress;
    }

    return job;
  }
};

export default JobsService;
